import {
  Box,
  Button,
  Drawer,
  Flex,
  Grid,
  MultiSelect,
  Select,
  Text,
} from "@mantine/core";
import { GoChevronDown } from "react-icons/go";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { useState } from "react";
import DateRangePicker from "../NoteReportModal/DateRangePicker";
import useStyles from "./style";
import { useSelector } from "react-redux";
import { pdf } from "@react-pdf/renderer";
import { TaskReportPdf } from "../TaskReportPdf";
import { getTaskReport } from "../../services/task";
import { Form8615ReportPdf } from "../Form8615Pdf";
import { decrypt } from "../../utils";

const TaskReportDrawer = ({ drawerOpen, drawerClose, reportType }) => {
  const { classes, cx } = useStyles();
  const [customError, setCustomError] = useState({});
  const { allPatients } = useSelector((state) => state?.patient);
  const taskReportLoading = useSelector(
    (state) => state?.task?.taskReportLoading
  );
  const { allFacilities } = useSelector((state) => state?.facility);
  const { staff } = useSelector((state) => state.staff);
  const { allSignature } = useSelector((state) => state?.signature);
  const { taskTypes } = useSelector((state) => state.task);

  const form = useForm({
    initialValues: {
      client: "",
      facility: "",
      taskTypes: [],
    },
    validate: {
      client: (value) => (value ? null : "Required"),
      facility: (value) => (value ? null : "Required"),
      taskTypes: (value) =>
        reportType === "task" ? (value?.length > 0 ? null : "Required") : null,
    },
  });
  const [dateRange, setDateRange] = useState({
    type: null,
    date: [],
  });
  const [period, setPeriod] = useState([]);
  const [opened, { open, close }] = useDisclosure(false);

  const isFormValid =
    form.values.client.length > 0 &&
    form.values.facility &&
    (reportType === "task" ? form.values.taskTypes.length > 0 : true) &&
    period[0] &&
    period[1];

  const facilityName =
    allFacilities?.facilities?.find(
      (facility) => facility.id === form.values.facility
    )?.name || "";
  const handleApply = () => {
    if (dateRange.date?.[0] && dateRange.date?.[1]) {
      setPeriod(dateRange.date);
      setCustomError((prev) => ({ ...prev, period: "" }));
      close();
    }
  };
  const handleCancel = () => {
    setPeriod([]);
    form.reset();
    setCustomError((prev) => ({ ...prev, period: "" }));
    setDateRange({
      type: null,
      date: [],
    });

    drawerClose();
  };

  const toDataURL = async (url) => {
    try {
      const res = await fetch(url, { mode: "cors" });
      const blob = await res.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      console.warn("Image load failed:", url);
      return null;
    }
  };

  const preloadAllImages = async (allSignature) => {
    const imageMap = {}; // store url → base64
    const imageUrls = new Set();

    allSignature?.forEach((sig) => {
      ["signature", "signatureImage", "signatureInitials"].forEach((key) => {
        if (sig?.[key]) {
          try {
            const decryptedUrl = decrypt(sig[key]);
            if (decryptedUrl) imageUrls.add(decryptedUrl);
          } catch {}
        }
      });
    });

    // Convert all URLs to Base64
    const promises = Array.from(imageUrls).map(async (url) => {
      const base64 = await toDataURL(url);
      if (base64) imageMap[url] = base64;
    });

    await Promise.all(promises);
    return imageMap;
  };

  const handleSubmit = async (values) => {
    const errors = {};
    if (!period[0] || !period[1]) {
      errors.period = "Required";
    }

    if (Object.keys(errors).length) {
      setCustomError(errors);
      return;
    }
    const payload = {
      facility: values.facility,
      client: values.client,
      taskTypes: values.taskTypes,
      startDate: period[0].toISOString(),
      endDate: period[1].toISOString(),
      reportType: reportType,
    };
    const imageMap = await preloadAllImages(allSignature);
    const res = await getTaskReport(payload);
    try {
      if (reportType === "task") {
        if (res) {
          const doc = (
            <TaskReportPdf
              data={res}
              dateRange={{ start: period[0], end: period[1] }}
              allSignature={allSignature}
              imageMap={imageMap}
            />
          );

          const asPdf = pdf();
          asPdf.updateContainer(doc);
          const blob = await asPdf.toBlob();

          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `Task_Report_${facilityName}.pdf`;
          link.click();
          URL.revokeObjectURL(url);

          handleCancel();
        }
      } else if (reportType === "form8615") {
        const doc = (
          <Form8615ReportPdf
            data={res}
            dateRange={{ start: period[0], end: period[1] }}
            allSignature={allSignature}
            imageMap={imageMap}
          />
        );
        const asPdf = pdf();
        asPdf.updateContainer(doc);
        const blob = await asPdf.toBlob();

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `Form8615_Report_${facilityName}.pdf`;
        link.click();
        URL.revokeObjectURL(url);
        handleCancel();
      }
    } catch (error) {
      console.log("error:", error);
    }
  };

  return (
    <Drawer
      opened={drawerOpen}
      offset={8}
      size="55%"
      sx={{
        ".mantine-Drawer-close": {
          color: "#000",
          "& svg": {
            width: "25px !important",
            height: "25px !important",
          },
        },
        ".mantine-11shxvp": {
          background: "#F5F5F5",
          borderTopLeftRadius: 16,
          borderBottomLeftRadius: 16,
        },
        ".mantine-Drawer-close:hover": {
          color: "#FFAF4A",
        },
        ".mantine-1q36a81": {
          padding: "0",
        },
      }}
      zIndex={1000}
      title={
        <Text fz={24} fw={700} ff="Inter" color="#000">
          {reportType === "form8615" ? "Form 8615" : "Task Report"}
        </Text>
      }
      position="right"
      overlayProps={{ backgroundOpacity: 2.5, blur: 4 }}
      shadow="md"
      onClose={handleCancel}
      centered
    >
      <Flex direction="column" py={14} px={24}>
        <form onSubmit={form.onSubmit((values) => handleSubmit(values))}>
          <Box className={classes.formBox}>
            <Grid gutter={24}>
              <Grid.Col span={6}>
                <Select
                  size="sm"
                  className={classes.inputField}
                  searchable
                  withAsterisk
                  label="Facility"
                  placeholder="Select Facility"
                  rightSectionPointerEvents="none"
                  rightSection={<GoChevronDown color="#94A3B8" size={16} />}
                  {...form.getInputProps("facility")}
                  onChange={(val) => {
                    form.setFieldValue("client", "");
                    form.setFieldValue("facility", val);
                  }}
                  data={
                    allFacilities && allFacilities?.facilities
                      ? allFacilities?.facilities
                          ?.filter(({ active, deleted }) => active && !deleted)
                          ?.map((opt) => ({
                            value: opt?.id,
                            label: opt?.name,
                          }))
                      : []
                  }
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <MultiSelect
                  searchable
                  label="Client"
                  disabled={!form.values.facility}
                  multiple
                  withAsterisk
                  size="sm"
                  className={cx(classes.inputField, classes.multiSelect)}
                  rightSection={<GoChevronDown color="#94A3B8" size={16} />}
                  placeholder="Select Client"
                  rightSectionPointerEvents="none"
                  data={[
                    { value: "ALL", label: "All Clients" }, // 👈 Add this at the top
                    ...(allPatients
                      ? allPatients
                          ?.filter(
                            (p) =>
                              p?.active === true &&
                              p?.deleted === false &&
                              p?.facilityId?.some(
                                (f) =>
                                  f?.id === form.values.facility &&
                                  f?.active === true &&
                                  f?.deleted === false
                              )
                          )
                          ?.map((opt) => ({
                            value: opt?.id,
                            label: opt?.name,
                          }))
                      : []),
                  ]}
                  value={form.values.client}
                  onChange={(val) => {
                    if (val.includes("ALL")) {
                      // get all clients for this facility
                      const facilityClients = allPatients
                        ?.filter(
                          (p) =>
                            p?.active === true &&
                            p?.deleted === false &&
                            p?.facilityId?.some(
                              (f) =>
                                f?.id === form.values.facility &&
                                f?.active === true &&
                                f?.deleted === false
                            )
                        )
                        ?.map((opt) => opt?.id);

                      // set all client ids
                      form.setFieldValue("client", facilityClients);
                    } else {
                      form.setFieldValue("client", val);
                    }
                  }}
                  error={form.errors.client}
                />
              </Grid.Col>
              {reportType === "task" && (
                <Grid.Col span={6}>
                  <MultiSelect
                    searchable
                    label="Task Type"
                    multiple
                    withAsterisk
                    size="sm"
                    className={cx(classes.inputField, classes.multiSelect)}
                    rightSection={<GoChevronDown color="#94A3B8" size={16} />}
                    placeholder="Select Task Type"
                    rightSectionPointerEvents="none"
                    data={[
                      { value: "ALL", label: "All Task Types" },
                      ...(taskTypes
                        ? taskTypes?.map((opt) => ({
                            value: opt?.id,
                            label: opt?.name,
                          }))
                        : []),
                    ]}
                    value={form.values.taskTypes}
                    onChange={(val) => {
                      if (val.includes("ALL")) {
                        const allTypes = taskTypes?.map((opt) => opt?.id) || [];
                        form.setFieldValue("taskTypes", allTypes);
                      } else {
                        form.setFieldValue("taskTypes", val);
                      }
                    }}
                    error={form.errors.taskTypes}
                  />
                </Grid.Col>
              )}
              <Grid.Col span={6}>
                <Text fz={14} fw={500} ff="Inter" color="#262626">
                  Period <span style={{ color: "red" }}>*</span>
                </Text>

                <DateRangePicker
                  dateRange={dateRange}
                  setDateRange={setDateRange}
                  handleApply={handleApply}
                  open={open}
                  opened={opened}
                  close={close}
                  size="md"
                  mt={20}
                  style={{ marginTop: "20px" }}
                />
                {customError.period && (
                  <Text color="red" fz={16} mt={4}>
                    {customError.period}
                  </Text>
                )}
              </Grid.Col>
            </Grid>
          </Box>
          <Flex gap={20} align="center" justify="flex-end">
            <Button
              className={classes.cancelBtn}
              onClick={() => handleCancel()}
            >
              Cancel
            </Button>
            <Button
              disabled={!isFormValid}
              type="submit"
              className={classes.createBtn}
              loading={taskReportLoading}
            >
              Create report
            </Button>
          </Flex>
        </form>
      </Flex>
    </Drawer>
  );
};

export default TaskReportDrawer;
